
import {
  HeaderFlyoutComponent,
  HeaderFlyoutData,
  HeaderFlyoutMethods,
  HeaderFlyoutComputed,
  HeaderFlyoutProps,
} from '@owc-packages/Header/src/HeaderFlyout';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import OwcLanguageSwitch from '@owc-packages/Header/src/LanguageSwitch.vue';
import OwcLogo from '@owc-packages/Header/src/HeaderLogo.vue';
import OwcHeaderButton from '@owc-packages/Header/src/HeaderButton.vue';
import OwcHeaderNavigationToolList from '@owc-packages/Header/src/HeaderNavigationToolList.vue';
import OwcHeaderNavigationLinkList from '@owc-packages/Header/src/HeaderNavigationLinkList.vue';
import OwcHeaderNavigationLink from '@owc-packages/Header/src/HeaderNavigationLink.vue';
import OwcHeaderNavigationTeaser from '@owc-packages/Header/src/HeaderNavigationTeaser.vue';
import headerMixin from '@owc-utils/Mixins/Header';
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import { desktopBreakpoints } from '@owc-utils/MediaQueryService/MediaQueryUtils';
import { lockWindowScroll, unlockWindowScroll } from '@owc-utils/common/LockWindow';
import { throttle } from '@owc-utils/Events';
import { THROTTLE_TIMER } from './Header.configs';
import { AccessibilityFocusManager } from '@owc-utils/common/AccessibilityFocusHelper';
import getAccessibilityStackInstance from '@owc-utils/common/AccessibilityFocusStackHelper';
import { FlyoutInnerItemsType } from './Header.enums';
import { setupHostApi, dispatchMainFlyoutCloseEvent } from '@owc-utils/Seamless/flyoutIntegrations';
import { updateActiveFlyout } from '@owc-utils/Seamless/headerIntegrations';
import { headerFlyoutFocusHandler } from '@owc-packages/Header/src/HeaderUtils';

/*
 * A flyout is a normal Vue component that can have X topics, entries or links inside of it
 *  Each topic can have inside of it another Flyout.
 *  FlyoutData is used to inform inner flyouts in which level they are open.
 *  this currentFlyoutLevel is calculated based on the parent of the flyout.
 *
 *  If parent == HeaderNavigation => level 1
 *  If parent == Topic => level 2 or 3.
 *
 * The destintion between 2 and 3 is not relevant since they are visually identical.
 */
const globalFocusStack = getAccessibilityStackInstance();
const scrollHeightTriggerPoint = 3;
let flyoutChainSize = 0;

export default (ComponentInitializeDispatcher as HeaderFlyoutComponent).extend<
  HeaderFlyoutData,
  HeaderFlyoutMethods,
  HeaderFlyoutComputed,
  HeaderFlyoutProps
>({
  name: 'owc-header-flyout',
  mixins: [headerMixin],
  components: {
    SlotContainer,
    OwcLogo,
    OwcHeaderButton,
    OwcLanguageSwitch,
    OwcHeaderNavigationLink,
    OwcHeaderNavigationTeaser,
    OwcHeaderNavigationToolList,
    OwcHeaderNavigationLinkList,
  },
  beforeCreate() {
    if (this.$options.components) {
      this.$options.components.OwcHeaderNavigationTopic = require('./HeaderNavigationTopic.vue').default;
    }
  },
  props: {
    flyoutTitle: {
      required: false,
      type: String,
    },
    languageConfigs: {
      required: false,
      type: Array,
    },
    imprint: {
      required: false,
      type: Object,
    },
    currentFlyoutLevel: {
      required: true,
      type: Number,
    },
    isFlyoutNavVisible: {
      required: true,
      type: Boolean,
    },
    isThisFlyoutVisible: {
      required: false,
      type: Boolean,
    },
    flyoutInnerItems: {
      required: false,
      type: Array,
    },
    /* Since the flyout is not a webcomponent
     * we have to pass this value directly
     **/
    isAuthorEditMode: {
      required: false,
      type: Boolean,
    },
    flyoutId: {
      required: false,
      type: String,
    },
    isSecondaryVmosFlyout: {
      required: false,
      type: Boolean,
    },
  },
  data() {
    return {
      isLastLevelDesktop: false,
      isFirstLevelDesktop: false,
      closeButtonLabel: '',
      backButtonLabel: '',
      accessibilityHelper: new AccessibilityFocusManager(),
      scrollHandler: null,
      shouldShowDesktopTopTitle: false,
    };
  },
  mounted() {
    if (this.onMQChange) {
      this.onMQChange(this.handleResize as EventListenerOrEventListenerObject);
    }
    if (this.getCurrentMediaQuery) {
      this.setFlyoutLevels(this.getCurrentMediaQuery());
    }

    this.storeStateChangeListener = this.onHeaderStateChange(state => {
      this.closeButtonLabel = state.navigationLabels?.closeButton;
      this.backButtonLabel = state.navigationLabels?.backButton;

      flyoutChainSize = state.flyoutChain?.length;
    });

    setupHostApi(this);
  },
  beforeDestroy() {
    if (this.storeStateChangeListener) {
      this.removeOnHeaderStateChange(this.storeStateChangeListener);
    }
  },
  methods: {
    initAccessibilityHelper() {
      const state = this.getHeaderStoreState();
      flyoutChainSize = state.flyoutChain?.length;

      if (!this.accessibilityHelper.isInitialized && this.$refs.flyoutInnerContent) {
        const innerContent = this.$refs.flyoutInnerContent;

        this.accessibilityHelper.init(innerContent, '.owc-header-flyout__navigation-list');
      }
    },
    throttleOpacityChange() {
      if (this.isFirstLevelDesktop) {
        if (!this.scrollHandler) {
          this.scrollHandler = throttle(THROTTLE_TIMER, this.calculateHeadlineOpacity);
        }
        this.scrollHandler();
      }
    },
    handleFlyoutCloseClick(isClose) {
      this.$el.classList.add('owc-header-flyout--allow-animation');

      if (this.isSecondaryVmosFlyout) {
        dispatchMainFlyoutCloseEvent(this, {
          isClose: isClose,
          flyoutId: this.flyoutId,
          newStatus: false,
          isInnerFlyout: true,
          isBackInteraction: !isClose,
        });
      } else {
        this.$emit('closeFlyout', isClose);
      }

      this.setOpenFlyoutId(null);
    },
    handleResize(event) {
      const currentMq = event.detail.current;
      const isDesktop = desktopBreakpoints.includes(currentMq);

      if (isDesktop && flyoutChainSize === 1) {
        this.closeHeaderFlyout(true);
      } else {
        this.setFlyoutLevels(currentMq);
      }
    },
    handleFlyoutIsOpen() {
      this.initAccessibilityHelper();

      if (this.accessibilityHelper.shouldManageFocus && this.currentFlyoutLevel === flyoutChainSize) {
        this.accessibilityHelper.focusOnNextElement();
      }

      if (this.isSecondaryVmosFlyout) {
        this.openHeaderFlyout([{ infoToAdd: this.flyoutId, flyoutLevel: 3 }]);
        updateActiveFlyout(this.$el, this.flyoutId, true, false, false);
      }
    },
    setFlyoutLevels(currentMq: string) {
      const isDesktop = desktopBreakpoints.includes(currentMq);

      this.isLastLevelDesktop = isDesktop && this.currentFlyoutLevel === 3;
      this.isFirstLevelDesktop = isDesktop && this.currentFlyoutLevel === 2;

      if (this.isThisFlyoutVisible) {
        if (isDesktop && flyoutChainSize <= 1) {
          unlockWindowScroll();
        } else if (!isDesktop && flyoutChainSize > 0) {
          lockWindowScroll();
        }
      }
    },
    /*
     * Find more information about this method on the Header documentation [Accessibility chapter]
     */
    keyDownHandler(event: KeyboardEvent) {
      const keyboardEvent = event;

      headerFlyoutFocusHandler(keyboardEvent, globalFocusStack, flyoutChainSize, this);
    },
    getInnerFlyoutElementType(elementType) {
      const type = FlyoutInnerItemsType[elementType];

      return type ? type : 'div';
    },
    /**
     * Calculate if flyout desktop headline should be shown based on the amount
     * scrolled. If the headline is already under a third of the top bar, show flyout
     */
    calculateHeadlineOpacity() {
      const navElement = this.$refs.flyoutInnerContent;

      if (navElement) {
        const topBarHeight = this.getElementHeight;

        this.shouldShowDesktopTopTitle = navElement.scrollTop > topBarHeight * scrollHeightTriggerPoint;
      }
    },
    stringifiedJsonData(componentData) {
      return JSON.stringify(Object.assign({}, this.childTopicLevel, componentData));
    },
    shouldAddVmosSlot(listItem, index) {
      return listItem.itemType === 'topic' && index === 0 && this.currentFlyoutLevel === 1;
    },
  },
  computed: {
    shouldShowVmos() {
      return Boolean(this.isSecondaryVmosFlyout) || this.getModelTopicId() === this.flyoutId;
    },
    isFirstLevelFlyout() {
      return this.currentFlyoutLevel === 1 || this.isFirstLevelDesktop;
    },
    childTopicLevel() {
      return {
        currentFlyoutLevel: this.currentFlyoutLevel,
      };
    },
    stringifiedSlotData() {
      return JSON.stringify(this.childTopicLevel);
    },
    getElementHeight() {
      const topBarElement = this.$refs.flyoutTopbar;

      return topBarElement ? topBarElement.getBoundingClientRect().height : 0;
    },
    languageConfigurations() {
      return this.languageConfigs && this.languageConfigs.length > 0;
    },
  },
});
